import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import moment from 'moment-timezone'
import Select from 'react-select'

import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'
import {
  deleteSummaryByPaymentMethodReportSubscription,
  getPaymentListingsV4,
  listSummaryByPaymentMethodReportSubscriptions,
  saveSummaryByPaymentMethodReportSubscription,
  sendSummaryByPaymentMethodReportTestEmail,
} from '../../utilities/api'
import {
  checkPermission,
  createWorkbook,
  saveWorkbook,
  setColumnsAndRows,
  setHeaders,
  addWorksheet,
  DEFAULTNUMHEADERS,
  currencyFormat,
  numberFormat,
  styleTotalRow,
  formatCurrency,
  formatNumber,
  formatString,
} from '../../utilities/helpers'
import {
  dateTime,
  constants,
  ORGANIZATION_PERMISSION_SLUGS,
} from '@blocktickets/shared-utils'

import { useReportRefresh } from '../../hooks/useReportRefresh'
import { ReportActions } from '../ReportActions'
import { DateInputWrapper } from '../DateInputWrapper'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import SavedReportScheduleFields, {
  DEFAULT_SAVED_REPORT_SCHEDULE,
} from '../PaymentListingsV4Wrapper/SavedReportScheduleFields'
import {
  buildDailySettlementQueryParams,
  formatDailySettlementReportDateLabel,
  isDailySettlementDateRangeInvalid,
} from '../DailySettlementWrapper/dailySettlementDateRange'
import {
  buildSummaryByPaymentMethodData,
  normalizePaymentMethodKey,
  sumMetricRows,
} from './summaryByPaymentMethodData'

import '../ClubSeatWrapper/report-print.scss'
import './summaryByPaymentMethod.scss'

const { PAYMENT_TYPE, PAYMENT_TYPE_LABEL } = constants

const DEFAULT_SAVED_REPORT_VALUE = 'default'

const EXCLUDED_PAYMENT_TYPE_OPTIONS = new Set([
  PAYMENT_TYPE.COMP,
  PAYMENT_TYPE.EXCHANGE,
  PAYMENT_TYPE.VOUCHER,
  PAYMENT_TYPE.RESERVED,
])

const BASE_PAYMENT_METHOD_OPTIONS = Object.values(PAYMENT_TYPE)
  .filter((value) => !EXCLUDED_PAYMENT_TYPE_OPTIONS.has(value))
  .map((value) => ({
    value,
    label: PAYMENT_TYPE_LABEL[value] || formatString(value),
  }))

const formatPaymentMethodLabel = (paymentMethod) =>
  paymentMethod ? formatString(paymentMethod) : '—'

export default function SummaryByPaymentMethodWrapper({ organization }) {
  const { isLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_SETTLEMENT_REPORT
    )

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [listings, setListings] = useState(null)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([])
  const [generatedAt, setGeneratedAt] = useState(null)

  const [savedReports, setSavedReports] = useState([])
  const [selectedReportId, setSelectedReportId] = useState(
    DEFAULT_SAVED_REPORT_VALUE
  )
  const [reportName, setReportName] = useState('')
  const [reportSchedule, setReportSchedule] = useState({
    ...DEFAULT_SAVED_REPORT_SCHEDULE,
  })
  const [reportError, setReportError] = useState('')
  const [reportMessage, setReportMessage] = useState('')
  const [isSavingReport, setIsSavingReport] = useState(false)
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)

  const selectedReport = useMemo(
    () =>
      savedReports.find(
        (report) => String(report.id) === String(selectedReportId)
      ),
    [savedReports, selectedReportId]
  )

  const currentReportFilters = useMemo(
    () => ({
      paymentMethods: selectedPaymentMethods.map((option) => option.value),
    }),
    [selectedPaymentMethods]
  )

  const data = useMemo(() => {
    if (!listings) return null
    return buildSummaryByPaymentMethodData(
      listings,
      currentReportFilters.paymentMethods
    )
  }, [listings, currentReportFilters.paymentMethods])

  const paymentMethodOptions = useMemo(() => {
    const fromData = (data?.availablePaymentMethods || []).map((opt) => ({
      value: opt.value,
      label: formatPaymentMethodLabel(opt.label || opt.value),
    }))
    const byValue = new Map()
    ;[...BASE_PAYMENT_METHOD_OPTIONS, ...fromData].forEach((opt) => {
      if (!opt?.value) return
      byValue.set(opt.value, opt)
    })
    return Array.from(byValue.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    )
  }, [data?.availablePaymentMethods])

  useLayoutEffect(() => {
    const root = document.getElementById('root')
    root?.classList.add('report-page')
    return () => {
      root?.classList.remove('report-page')
    }
  }, [])

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    hideLoading()
  }, [orgPermissionsReady, hasPermission, hideLoading])

  const loadSavedReports = useCallback(async () => {
    if (!organization?.uuid || !hasPermission) return
    try {
      const res = await listSummaryByPaymentMethodReportSubscriptions(
        organization.uuid
      )
      const reports = Array.isArray(res?.data?.subscriptions)
        ? res.data.subscriptions
        : []
      setSavedReports(reports)
    } catch (error) {
      console.error(
        'Error loading saved Summary by Payment Method reports:',
        error
      )
    }
  }, [organization?.uuid, hasPermission])

  useEffect(() => {
    loadSavedReports()
  }, [loadSavedReports])

  const isDateRangeInvalid = isDailySettlementDateRangeInvalid(fromDate, toDate)

  useEffect(() => {
    const fetchData = async () => {
      if (!fromDate || isDateRangeInvalid) return
      setIsLoadingData(true)

      try {
        const tz = dateTime.getBrowserTimezone('UTC')
        const { fromUtcIso, toUtcIso, options } =
          buildDailySettlementQueryParams(fromDate, toDate, tz)

        const res = await getPaymentListingsV4(
          organization.uuid,
          fromUtcIso,
          toUtcIso,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          options
        )

        setListings(res?.data || [])
        setGeneratedAt(new Date())
      } catch (error) {
        console.error('Error fetching summary by payment method:', error)
        setListings(null)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    if (!orgPermissionsReady || !hasPermission) return
    fetchData()
  }, [
    fromDate,
    toDate,
    isDateRangeInvalid,
    organization?.uuid,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const resetToDefaultReport = () => {
    setSelectedReportId(DEFAULT_SAVED_REPORT_VALUE)
    setReportName('')
    setReportSchedule({
      ...DEFAULT_SAVED_REPORT_SCHEDULE,
      email: AuthService.getUser()?.user?.email || '',
    })
    setSelectedPaymentMethods([])
    setReportError('')
    setReportMessage('')
  }

  const applySavedReport = (reportId) => {
    if (!reportId || reportId === DEFAULT_SAVED_REPORT_VALUE) {
      resetToDefaultReport()
      return
    }

    const report = savedReports.find(
      (item) => String(item.id) === String(reportId)
    )
    if (!report) {
      resetToDefaultReport()
      return
    }

    const filters = report.filters || {}
    const methods = Array.isArray(filters.paymentMethods)
      ? filters.paymentMethods
          .map((value) => {
            const key = normalizePaymentMethodKey(value)
            const option =
              paymentMethodOptions.find((opt) => opt.value === key) ||
              BASE_PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === key)
            return (
              option || {
                value: key,
                label: formatPaymentMethodLabel(key),
              }
            )
          })
          .filter(Boolean)
      : []

    setSelectedReportId(report.id)
    setReportName(report.name || '')
    setReportSchedule({
      ...DEFAULT_SAVED_REPORT_SCHEDULE,
      ...(report.schedule || {}),
      email: report.schedule?.email || AuthService.getUser()?.user?.email || '',
    })
    setSelectedPaymentMethods(methods)
    setReportError('')
    setReportMessage('')
  }

  const buildReportPayload = ({ updateExisting = false } = {}) => ({
    id: updateExisting && selectedReport ? selectedReport.id : undefined,
    organizationUUID: organization.uuid,
    name: reportName.trim(),
    filters: currentReportFilters,
    schedule: reportSchedule,
    exportFormat: 'xlsx',
  })

  const handleSaveReport = async ({ updateExisting = false } = {}) => {
    const name = reportName.trim()
    if (!name) {
      setReportError('Enter a name for this saved report.')
      return
    }
    if (reportSchedule.enabled && !String(reportSchedule.email || '').trim()) {
      setReportError('Enter an email address for scheduled delivery.')
      return
    }

    setIsSavingReport(true)
    setReportError('')
    setReportMessage('')

    try {
      const res = await saveSummaryByPaymentMethodReportSubscription(
        buildReportPayload({ updateExisting })
      )
      const saved = res?.data
      const listRes = await listSummaryByPaymentMethodReportSubscriptions(
        organization.uuid
      )
      const reports = Array.isArray(listRes?.data?.subscriptions)
        ? listRes.data.subscriptions
        : []
      setSavedReports(reports)
      const active =
        reports.find((report) => String(report.id) === String(saved?.id)) ||
        saved
      if (active?.id) {
        setSelectedReportId(active.id)
        setReportName(active.name || name)
        setReportSchedule({
          ...DEFAULT_SAVED_REPORT_SCHEDULE,
          ...(active.schedule || reportSchedule),
        })
      }
      setReportMessage(
        reportSchedule.enabled
          ? 'Report saved with email schedule.'
          : 'Report saved.'
      )
    } catch (error) {
      console.error('Error saving Summary by Payment Method report:', error)
      setReportError(
        error?.response?.data?.error?.message ||
          'Could not save this report. Please try again.'
      )
    } finally {
      setIsSavingReport(false)
    }
  }

  const handleDeleteReport = async () => {
    if (!selectedReport) return

    setIsSavingReport(true)
    setReportError('')
    setReportMessage('')

    try {
      await deleteSummaryByPaymentMethodReportSubscription(
        selectedReport.id,
        organization.uuid
      )
      setSavedReports((prev) =>
        prev.filter((report) => String(report.id) !== String(selectedReport.id))
      )
      resetToDefaultReport()
      setReportMessage('Saved report deleted.')
    } catch (error) {
      console.error('Error deleting Summary by Payment Method report:', error)
      setReportError(
        error?.response?.data?.error?.message ||
          'Could not delete this report. Please try again.'
      )
    } finally {
      setIsSavingReport(false)
    }
  }

  const handleSendTestReportEmail = async () => {
    const name =
      reportName.trim() || selectedReport?.name || 'Summary by Payment Method'
    if (reportSchedule.enabled && !String(reportSchedule.email || '').trim()) {
      setReportError('Enter an email address before sending a test.')
      return
    }

    setIsSendingTestEmail(true)
    setReportError('')
    setReportMessage('')

    try {
      const res = await sendSummaryByPaymentMethodReportTestEmail({
        organizationUUID: organization.uuid,
        name,
        filters: currentReportFilters,
        schedule: {
          ...reportSchedule,
          email:
            reportSchedule.email || AuthService.getUser()?.user?.email || '',
        },
        exportFormat: 'xlsx',
      })
      const email = res?.data?.email || reportSchedule.email
      setReportMessage(
        email
          ? `Test email queued for ${email}.`
          : 'Test email queued successfully.'
      )
    } catch (error) {
      console.error(
        'Error sending Summary by Payment Method test email:',
        error
      )
      setReportError(
        error?.response?.data?.error?.message ||
          'Could not send test email. Please try again.'
      )
    } finally {
      setIsSendingTestEmail(false)
    }
  }

  const handleExcelExport = async () => {
    try {
      if (!orgPermissionsReady || !hasPermission) return
      if (
        !data ||
        (!data.byPaymentMethod?.length && !data.byProductOrEvent?.length)
      ) {
        return
      }

      const reportDate = formatDailySettlementReportDateLabel(fromDate, toDate)
      const organizationName = organization?.name ?? ''
      const headerLines = reportDate ? [`Date: ${reportDate}`] : []

      const workbook = createWorkbook()

      const metricColumns = [
        'name',
        'order_count',
        'quantity',
        'gross',
        'facility_fee',
      ]

      const formatPaymentRow = (row) => ({
        name: formatPaymentMethodLabel(row.paymentMethod ?? row.name),
        order_count: row.orderCount ?? 0,
        quantity: row.ticketsSold ?? 0,
        gross: Number(row.grossSales ?? 0),
        facility_fee: Number(row.facilityFees ?? 0),
      })

      const formatProductRow = (row) => ({
        name: row.name,
        order_count: row.orderCount ?? 0,
        quantity: row.ticketsSold ?? 0,
        gross: Number(row.grossSales ?? 0),
        facility_fee: Number(row.facilityFees ?? 0),
      })

      const writeMetricSheet = (title, rows, formatRow) => {
        const sheet = addWorksheet(title, workbook)
        setHeaders(title, organizationName, sheet, headerLines)

        const totals = sumMetricRows(rows)
        const exportRows = totals
          ? [
              ...rows.map(formatRow),
              formatRow({
                paymentMethod: 'Total',
                name: 'Total',
                ...totals,
              }),
            ]
          : rows.map(formatRow)

        setColumnsAndRows(metricColumns, exportRows, (r) => r, sheet)

        const headerRows = DEFAULTNUMHEADERS + headerLines.length
        const headerRow = sheet.getRow(headerRows)
        ;[
          title.includes('Product') ? 'Product / Event' : 'Payment Method',
          'Order Count',
          'Quantity',
          'Gross',
          'Facility Fee',
        ].forEach((label, idx) => {
          headerRow.getCell(idx + 1).value = label
        })
        headerRow.commit?.()

        numberFormat(2, sheet, headerRows)
        numberFormat(3, sheet, headerRows)
        currencyFormat(4, sheet, headerRows)
        currencyFormat(5, sheet, headerRows)

        if (totals) {
          styleTotalRow(sheet.lastRow, sheet, false)
        }
      }

      writeMetricSheet(
        'Summary by Payment Method',
        data.byPaymentMethod ?? [],
        formatPaymentRow
      )
      writeMetricSheet(
        'Summary by Product or Event',
        data.byProductOrEvent ?? [],
        formatProductRow
      )

      await saveWorkbook(workbook, 'summary by payment method')
    } catch (error) {
      console.error('Excel export failed:', error)
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  const reportDateLabel = formatDailySettlementReportDateLabel(fromDate, toDate)
  const generatedLabel = generatedAt
    ? moment(generatedAt).format('M/D/YYYY, h:mm:ss A')
    : ''

  return (
    <section
      className="spacer-sm summary-by-payment-method-report"
      data-testid="summary-by-payment-method-report-section"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Summary by Payment Method Report</h1>
        </div>
        <p className="section-header-desc">
          Order count, quantity, gross, and facility fee totals by payment
          method and by product or event.
        </p>
      </header>

      <ReportActions
        isLoading={isLoadingData}
        onRefresh={refresh}
        isRefreshing={isLoadingData}
        selected={fromDate}
        downloadReport={handleExcelExport}
        data={
          !fromDate || isDateRangeInvalid
            ? null
            : (data?.byPaymentMethod ?? data?.byProductOrEvent)
        }
      >
        <div style={{ width: '220px' }}>
          <Form.Group controlId="summary-pm-start-group">
            <Form.Label htmlFor="summary-pm-start">Start Date</Form.Label>
            <DateInputWrapper
              id="summary-pm-start"
              startDate={fromDate}
              selectedDate={fromDate}
              setDate={setFromDate}
              showMinDate={false}
              isReadOnly={isLoadingData}
              size="sm"
              styles="form-control-height"
            />
          </Form.Group>
        </div>
        <div style={{ width: '220px' }}>
          <Form.Group controlId="summary-pm-end-group">
            <Form.Label htmlFor="summary-pm-end">End Date</Form.Label>
            <DateInputWrapper
              id="summary-pm-end"
              startDate={fromDate}
              selectedDate={toDate}
              setDate={setToDate}
              showMinDate={false}
              isReadOnly={isLoadingData || !fromDate}
              size="sm"
              styles="form-control-height"
              error={isDateRangeInvalid}
            />
          </Form.Group>
        </div>
        <Form.Group
          controlId="summary-pm-payment-methods"
          style={{ minWidth: '280px', flex: 1 }}
        >
          <Form.Label className="uppercase text-body-xs font-weight-bold">
            Payment Methods
          </Form.Label>
          <Select
            isMulti
            isClearable
            name="paymentMethods"
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder="All Payment Methods"
            options={paymentMethodOptions}
            value={selectedPaymentMethods}
            onChange={(options) => setSelectedPaymentMethods(options || [])}
            isDisabled={isLoadingData}
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: '47.59px',
                fontSize: '14px',
                backgroundColor: '#fcfcfd',
                borderWidth: '2px',
                borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                borderRadius: '12px',
                '&:hover': {
                  borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                },
                boxShadow: state.isFocused
                  ? '0 0 0 0.25rem rgba(62, 139, 247, 0.25)'
                  : null,
              }),
              valueContainer: (base) => ({
                ...base,
                padding: '0 8px',
              }),
              input: (base) => ({
                ...base,
                margin: 0,
                padding: 0,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: '45.59px',
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#eef5ff',
                borderRadius: '20px',
                padding: '2px 8px',
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: '#3e8bf7',
                fontSize: '13px',
                fontWeight: '500',
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: '#3e8bf7',
                ':hover': {
                  backgroundColor: 'transparent',
                  color: '#3e8bf7',
                },
              }),
            }}
          />
        </Form.Group>
        <Form.Group controlId="savedReportSelect" style={{ width: '240px' }}>
          <Form.Label>Saved Report</Form.Label>
          <Form.Select
            className="form-control-height"
            value={selectedReportId}
            onChange={(e) => applySavedReport(e.target.value)}
            disabled={isLoadingData || isSavingReport}
          >
            <option value={DEFAULT_SAVED_REPORT_VALUE}>Default view</option>
            {savedReports.map((report) => (
              <option key={report.id} value={report.id}>
                {report.name}
                {report.schedule?.enabled ? ' (scheduled)' : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Button
          variant="outline-light"
          className="icon-button form-control-height d-flex align-items-center gap-2 px-4"
          onClick={() => {
            setReportError('')
            setReportMessage('')
            if (selectedReport) {
              setReportName(selectedReport.name || '')
              setReportSchedule({
                ...DEFAULT_SAVED_REPORT_SCHEDULE,
                ...(selectedReport.schedule || {}),
                email:
                  selectedReport.schedule?.email ||
                  AuthService.getUser()?.user?.email ||
                  '',
              })
            } else if (!reportSchedule.email) {
              setReportSchedule((prev) => ({
                ...prev,
                email: AuthService.getUser()?.user?.email || '',
              }))
            }
            setShowSaveModal(true)
          }}
          style={{
            borderRadius: '20px',
            borderColor: '#e6e8ec',
            color: '#121923',
            fontWeight: '600',
          }}
        >
          Save / Schedule
        </Button>
      </ReportActions>

      {isDateRangeInvalid && (
        <Form.Text
          className="text-danger d-block mb-4"
          data-testid="summary-by-payment-method-date-range-error"
        >
          End date cannot be before start date.
        </Form.Text>
      )}

      <div className="summary-by-payment-method-print-header d-none d-print-block mb-3">
        <h1>Summary by Payment Method Report</h1>
        <div className="fw-bold">{organization?.name}</div>
        {reportDateLabel && <div>Date: {reportDateLabel}</div>}
        {generatedLabel && <div>Generated: {generatedLabel}</div>}
      </div>

      <Results
        isLoading={isLoadingData}
        data={data}
        fromDate={fromDate}
        isDateRangeInvalid={isDateRangeInvalid}
      />

      <Modal
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Save / Schedule Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="savedReportName">
            <Form.Label className="uppercase text-body-xs font-weight-bold">
              Report name
            </Form.Label>
            <Form.Control
              type="text"
              className="form-control-height"
              value={reportName}
              onChange={(e) => {
                setReportName(e.target.value)
                if (reportError) setReportError('')
                if (reportMessage) setReportMessage('')
              }}
              placeholder="e.g. Weekly cash and check"
              maxLength={60}
              disabled={isSavingReport || isSendingTestEmail}
            />
          </Form.Group>
          <p className="text-muted small mt-2 mb-0">
            Saves the current payment method filters. Optionally email the
            matching report on a schedule (previous day / week / month /
            quarter).
          </p>
          <SavedReportScheduleFields
            schedule={reportSchedule}
            onChange={setReportSchedule}
            disabled={isSavingReport || isSendingTestEmail}
          />
          {reportError ? (
            <p className="text-danger small mt-2 mb-0">{reportError}</p>
          ) : null}
          {reportMessage ? (
            <p className="text-success small mt-2 mb-0">{reportMessage}</p>
          ) : null}
          <Stack direction="horizontal" gap={2} className="mt-3 flex-wrap">
            <Button
              variant="outline-light"
              className="btn-pill btn-cancel"
              disabled={isSavingReport || isSendingTestEmail}
              onClick={() => handleSaveReport({ updateExisting: false })}
            >
              {isSavingReport ? 'Saving…' : 'Save as new report'}
            </Button>
            {selectedReport && (
              <>
                <Button
                  variant="primary"
                  className="btn-pill btn-save"
                  disabled={isSavingReport || isSendingTestEmail}
                  onClick={() => handleSaveReport({ updateExisting: true })}
                >
                  Update report
                </Button>
                <Button
                  variant="outline-danger"
                  className="btn-pill"
                  disabled={isSavingReport || isSendingTestEmail}
                  onClick={handleDeleteReport}
                >
                  Delete report
                </Button>
              </>
            )}
            <Button
              variant="outline-light"
              className="btn-pill btn-cancel"
              disabled={isSavingReport || isSendingTestEmail}
              onClick={handleSendTestReportEmail}
            >
              {isSendingTestEmail ? 'Sending…' : 'Send test email'}
            </Button>
          </Stack>
        </Modal.Body>
      </Modal>
    </section>
  )
}

function Results({ isLoading, data, fromDate, isDateRangeInvalid }) {
  if (!fromDate || isDateRangeInvalid) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Select a start date to run this report.</p>
          <p className="subtitle subtitle--dark">
            Optionally filter by payment method or load a saved report template.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  const byPaymentMethod = data?.byPaymentMethod ?? []
  const byProductOrEvent = data?.byProductOrEvent ?? []
  const hasData = byPaymentMethod.length > 0 || byProductOrEvent.length > 0

  if (!hasData) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No orders found for the selected filters.</p>
          <p className="subtitle subtitle--dark">
            Try a different date range or payment method selection.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <div className="summary-by-payment-method-results">
      <MetricTable
        title="Summary by Payment Method"
        firstColumnLabel="Payment Method"
        rows={byPaymentMethod}
        getLabel={(row) => formatPaymentMethodLabel(row.paymentMethod)}
        rowKey={(row, idx) => `${row.paymentMethod}-${idx}`}
      />
      <MetricTable
        title="Summary by Product or Event"
        firstColumnLabel="Product / Event"
        rows={byProductOrEvent}
        getLabel={(row) => row.name}
        rowKey={(row, idx) => `${row.name}-${idx}`}
      />
    </div>
  )
}

function MetricTable({ title, firstColumnLabel, rows, getLabel, rowKey }) {
  const totals = sumMetricRows(rows)

  return (
    <Card body className="card--md mb-4">
      <Card.Title as="h5" className="mb-3">
        {title}
      </Card.Title>
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height">
          <Table>
            <thead>
              <tr className="md">
                <th className="uppercase text-body">{firstColumnLabel}</th>
                <th className="uppercase text-body text-end">Order Count</th>
                <th className="uppercase text-body text-end">Quantity</th>
                <th className="uppercase text-body text-end">Gross</th>
                <th className="uppercase text-body text-end">Facility Fee</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={rowKey(row, idx)} className="md-x">
                  <td className="whitespace-nowrap">{getLabel(row)}</td>
                  <td className="whitespace-nowrap text-end">
                    {formatNumber(row.orderCount)}
                  </td>
                  <td className="whitespace-nowrap text-end">
                    {formatNumber(row.ticketsSold)}
                  </td>
                  <td className="whitespace-nowrap text-end">
                    {formatCurrency(row.grossSales)}
                  </td>
                  <td className="whitespace-nowrap text-end">
                    {formatCurrency(row.facilityFees ?? 0)}
                  </td>
                </tr>
              ))}
              {totals && (
                <tr className="total-row dark md">
                  <td className="text-uppercase">Total</td>
                  <td className="whitespace-nowrap text-end">
                    {formatNumber(totals.orderCount)}
                  </td>
                  <td className="whitespace-nowrap text-end">
                    {formatNumber(totals.ticketsSold)}
                  </td>
                  <td className="whitespace-nowrap text-end">
                    {formatCurrency(totals.grossSales)}
                  </td>
                  <td className="whitespace-nowrap text-end">
                    {formatCurrency(totals.facilityFees)}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  )
}
